import { Flag, ChevronDown } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';

// Header for the Carrier Engagement referral drawer. Mirrors the carrier-portal
// drawer's information hierarchy but uses CE's status-bar layout (Base Status
// first) instead of CP's workflow timeline.
//
// Scope note (Referral Drawer Baseline): the flag indicators are visual
// placeholders only — toggle behaviour lives under the Referral Flags epic.
// The SL Response/Reason/Action fields are read-only here; clicking one calls
// onEditSl(field) so the inline-edit story can wire the composer later. The
// `—` placeholder renders whenever a value is unset (all six status fields are
// currently null from the API until CE/CP reason scoping is designed).

const DASH = '—';

// IDR / Prepay / HCC / SIU. value stays undefined for now — purely a placeholder.
const FLAGS = [
  { key: 'idr', label: 'IDR' },
  { key: 'prepay', label: 'Prepay' },
  { key: 'hcc', label: 'HCC' },
  { key: 'siu', label: 'SIU' },
];

// Private layout helpers — not exported.
function Detail({ label, children, className }) {
  return (
    <div className={cn('shrink-0 space-y-0.5 text-left', className)}>
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

// A status-bar field that reads as plain text. Used for Base/TPA fields that
// aren't editable from the drawer.
function StatusField({ label, value, className }) {
  return (
    <Detail label={label} className={className}>
      <span className={cn(value ? 'text-foreground' : 'text-muted-foreground')}>
        {value || DASH}
      </span>
    </Detail>
  );
}

// SL field rendered as a clickable affordance with a chevron. Editing is
// deferred to a separate story; onEditSl is invoked so it can be wired later.
function SlField({ label, field, value, onEditSl, className }) {
  return (
    <Detail label={label} className={className}>
      <button
        type="button"
        onClick={() => onEditSl?.(field)}
        aria-label={label}
        data-testid={`sl-field-${field}`}
        className="flex items-center gap-1 text-sm text-left hover:text-primary transition-colors"
      >
        <span className={cn(value ? 'text-foreground' : 'text-muted-foreground')}>
          {value || DASH}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </button>
    </Detail>
  );
}

function EngagementDrawerHeader({ referral, onEditSl }) {
  const agingOut = !!referral?.aging_out;
  const age = referral?.referral_age;
  const hasAge = age !== null && age !== undefined;

  return (
    <div
      data-testid="engagement-drawer-header"
      className="ml-4 mr-10 mt-4 mb-0 px-5 pt-4 pb-3 rounded-xl bg-card border border-border"
    >
      {/* Row 1 — identity + flags */}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
        <Detail label="Referral ID">
          <span data-testid="drawer-referral-id" className="text-xl font-semibold text-primary">
            {referral?.referral_id ?? DASH}
          </span>
        </Detail>

        <StatusField label="Client" value={referral?.client} className="min-w-[120px]" />
        <StatusField label="TPA/Carrier" value={referral?.tpa_carrier} className="min-w-[120px]" />

        <Detail label="Referral Type">
          {referral?.referral_type ? (
            <Badge variant="outline" className="font-medium text-xs px-2 py-0 h-5">
              {referral.referral_type}
            </Badge>
          ) : (
            <span className="text-muted-foreground">{DASH}</span>
          )}
        </Detail>

        <Detail label="Referral Name" className="min-w-[200px] max-w-[280px]">
          <span
            data-testid="drawer-referral-name"
            className="truncate block"
            title={referral?.referral_name || ''}
          >
            {referral?.referral_name || DASH}
          </span>
        </Detail>

        <Detail label="Referral Age">
          <div className="flex items-center gap-2">
            <span className={cn(hasAge ? 'text-foreground' : 'text-muted-foreground')}>
              {hasAge ? `${age} days` : DASH}
            </span>
            {agingOut && (
              <span
                data-testid="aging-out-badge"
                className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-destructive/10 border border-destructive/20"
              >
                <Flag className="h-3.5 w-3.5 text-destructive" />
                <span className="text-xs font-medium text-destructive">Aging Out</span>
              </span>
            )}
          </div>
        </Detail>

        {/* Flag placeholders — non-functional in this story (Referral Flags epic). */}
        <div className="ml-auto flex items-start gap-5 pt-0.5" data-testid="flag-indicators">
          {FLAGS.map(({ key, label }) => (
            <div key={key} className="flex flex-col items-center gap-1">
              <p className="text-xs font-semibold text-foreground">{label}</p>
              <Flag className="h-4 w-4 text-muted-foreground" />
            </div>
          ))}
        </div>
      </div>

      {/* Row 2 — status bar. Base Status leads (CE-only addition). */}
      <div className="mt-3 flex flex-wrap items-start gap-3">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 bg-primary/5 rounded-lg py-2 px-3">
          <StatusField label="Base Status" value={referral?.base_status} className="min-w-[130px]" />
          <StatusField label="TPA/Carrier Status" value={referral?.tpa_carrier_status} className="min-w-[130px]" />
          <StatusField label="TPA/Carrier Reason" value={referral?.tpa_carrier_reason} className="min-w-[150px]" />
        </div>

        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 bg-muted/40 border border-border rounded-lg py-2 px-3">
          <SlField label="SL Response" field="sl_response" value={referral?.sl_response} onEditSl={onEditSl} className="min-w-[120px]" />
          <SlField label="SL Reason" field="sl_reason" value={referral?.sl_reason} onEditSl={onEditSl} className="min-w-[140px]" />
          <SlField label="SL Action" field="sl_action" value={referral?.sl_action} onEditSl={onEditSl} className="min-w-[110px]" />
        </div>
      </div>
    </div>
  );
}

export { EngagementDrawerHeader };
